import { useAssignGuardian } from 'common/queries-and-mutations/guardian';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import { Icon, Icons } from 'components/Icon';
import SuccessModal from 'components/Modal/SuccessModal';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Guardians } from 'service/dto/guardian.dto';
import { Children } from 'service/dto/children.dto';
import * as Yup from 'yup';
import { InfoModal } from 'components/Modal';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { GuardianTypeEnum } from '../types/user.enum.type';
import GuardianChildrenChecklist from './GuardianChildrenChecklist';

type Props = {
  type: GuardianTypeEnum;
  guardians: Guardians;
  childrens: Children;
  selectedChildIds: number[];
  onSelectedChildIdsChange: (ids: number[]) => void;
  onClose: () => void;
  onAddGuardianClick: () => void;
  onAssignGuardianComplete: () => void;
  onSkip?: () => void;
};

const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function AssignGuardianModal({
  type,
  guardians,
  childrens,
  selectedChildIds,
  onSelectedChildIdsChange,
  onClose,
  onAssignGuardianComplete,
  onAddGuardianClick,
  onSkip
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useAssignGuardian();

  const formik = useFormik({
    initialValues: {
      guardian: null
    },
    validationSchema: Yup.object({
      guardian: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string()
        })
        .nullable()
        .required('Please select a guardian')
    }),
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      assignGuardian(values);
    },
    enableReinitialize: true
  });

  const assignGuardian = (values: any) => {
    setIsSubmitting(true);
    mutate({
      guardianId: values.guardian.value,
      childrenIds: selectedChildIds,
      type
    });
  };

  const formatGuardians = useMemo(() => {
    return guardians.map(guardian => ({
      value: guardian.id!,
      label: `${[
        guardian.guardianFirstName,
        guardian.guardianMiddleName,
        guardian.guardianLastName
      ]
        .filter(Boolean)
        .join(' ')} (${guardian.guardianType})`
    }));
  }, [guardians]);

  //Success stays on screen until the parent remounts this modal for the
  //next round, so a stale child list is never shown
  const onProccessClick = () => {
    if (isCompleting) return;
    setIsCompleting(true);
    onAssignGuardianComplete();
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setShowSuccess(true);
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <>
      {showSuccess ? (
        <EnhancedSuccessModal
          title="Guardian Successfully Assigned"
          onProccessClick={onProccessClick}
          className="w-[20%]"
        />
      ) : (
        <InfoModal
          width="w-[55%]"
          className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
        >
          <div>
            <div className="flex flex-row items-center justify-end pl-6">
              <button type="button" onClick={onClose}>
                <Icon name={Icons.Close} />
              </button>
            </div>
            <div className="flex flex-row items-center pl-4">
              <p className="text-lg font-bold text-[#353535]">
                {type === GuardianTypeEnum.PRIMARY
                  ? 'Your child is under 18, please assign a primary guardian.'
                  : 'Assign a substitute guardian (optional).'}
              </p>
            </div>
            <div className="flex flex-col items-center justify-center">
              <form
                autoComplete="false"
                className="mt-10 flex w-full flex-col items-center justify-center px-6 md:w-[60%]"
                onSubmit={formik.handleSubmit}
              >
                <div className="mb-8 w-full">
                  <GuardianChildrenChecklist
                    childrens={childrens}
                    selectedIds={selectedChildIds}
                    onChange={onSelectedChildIdsChange}
                  />
                </div>
                <span className="flex">
                  <label className="mb-2 text-[12px] font-medium text-[#353535]">
                    Select existing guardian
                  </label>
                </span>
                <CustomSelect
                  options={formatGuardians}
                  selectedOption={formik.values.guardian}
                  handleOptionChange={option => {
                    formik.setFieldValue(`guardian`, option);
                  }}
                  name="guardian"
                  isSearchable
                  className="h-[3rem] rounded-xl"
                  error={formik.touched.guardian ? formik.errors.guardian : ''}
                />
                <div className="flex justify-center gap-6">
                  {onSkip && (
                    <Button
                      variant="secondary"
                      size="md"
                      className="my-5 mt-8 w-[10rem] rounded p-[0.2rem]"
                      label="Skip"
                      type="button"
                      onClick={onSkip}
                    ></Button>
                  )}
                  <Button
                    variant="primary"
                    size="md"
                    className={`col-start-2 col-end-2 my-5 mt-8 w-[10rem] rounded p-[0.2rem]`}
                    label="Assign"
                    type="submit"
                    disabled={!formik.isValid || !selectedChildIds.length}
                    loading={isSubmitting || isLoading}
                  ></Button>
                </div>
              </form>
              <button
                type="button"
                onClick={onAddGuardianClick}
                className="my-10 text-lg font-bold text-primary-900 underline"
              >
                Add New Guardian
              </button>
            </div>
          </div>
        </InfoModal>
      )}
    </>
  );
}
